import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAttendance } from '../context/AttendanceContext';
import { Link } from 'react-router-dom';

const { FiUsers, FiCheck, FiX, FiClock, FiSearch, FiAlertCircle, FiCheckCircle, FiCalendar, FiLock } = FiIcons;

const QRCheckIn = () => {
  const { members, checkInMember, getTodaysMeeting, isTuesday, isMeetingTime, isTodayMeetingCancelled } = useAttendance();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);
  const [checkInSuccess, setCheckInSuccess] = useState(false);
  const [checkInError, setCheckInError] = useState('');
  const [currentTime] = useState(new Date());
  const [isCheckInAllowed, setIsCheckInAllowed] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);

  const todaysMeeting = getTodaysMeeting();
  const activeMembers = members.filter(m => m.active);
  const filteredMembers = activeMembers.filter(member =>
    member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (member.company && member.company.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  useEffect(() => {
    // Determine if check-in is allowed based on day and time
    const now = new Date();
    const isTues = now.getDay() === 2; // 0 is Sunday, 2 is Tuesday
    const hour = now.getHours();
    const isCheckInTime = hour >= 7 && hour < 9;
    setIsCheckInAllowed(isTues && isCheckInTime && !isTodayMeetingCancelled());
  }, [isTodayMeetingCancelled]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setSelectedMember(null);
  };

  const handleSelectMember = (member) => {
    if (!isCheckInAllowed) return;
    setSelectedMember(member);
    setCheckInSuccess(false);
    setCheckInError('');
  };

  const handleCheckIn = async () => {
    if (!selectedMember || !isCheckInAllowed) return;
    try {
      await checkInMember(selectedMember.id);
      setCheckInSuccess(true);
      setCheckInError('');
      setSelectedMember(null);
      setSearchTerm('');
      setShowThankYou(true);
    } catch (error) {
      setCheckInError(error.message || 'Failed to check in');
      setCheckInSuccess(false);
    }
  };

  const getMeetingStatus = () => {
    if (!isTuesday()) {
      return {
        message: 'No meeting today - Meetings are on Tuesdays only',
        color: 'text-gray-600',
        bgColor: 'bg-gray-50 border-gray-200'
      };
    }

    if (isTodayMeetingCancelled()) {
      return {
        message: 'Today\'s meeting has been cancelled',
        color: 'text-red-600',
        bgColor: 'bg-red-50 border-red-200'
      };
    }

    const now = new Date();
    const currentHour = now.getHours();
    const currentMinutes = now.getMinutes();
    const currentTime = currentHour * 60 + currentMinutes;

    if (currentTime < 7 * 60) {
      // Before 7:00 AM
      return {
        message: 'Check-in opens at 7:00 AM',
        color: 'text-blue-600',
        bgColor: 'bg-blue-50 border-blue-200'
      };
    } else if (currentTime < 8 * 60 + 5) {
      // Before 8:05 AM
      return {
        message: 'Check-in period - On time',
        color: 'text-green-600',
        bgColor: 'bg-green-50 border-green-200'
      };
    } else if (currentTime < 9 * 60) {
      // Before 9:00 AM
      return {
        message: 'Late check-in period',
        color: 'text-orange-600',
        bgColor: 'bg-orange-50 border-orange-200'
      };
    } else {
      return {
        message: 'Check-in closed',
        color: 'text-red-600',
        bgColor: 'bg-red-50 border-red-200'
      };
    }
  };

  const isLate = () => {
    const currentHour = currentTime.getHours();
    const currentMinutes = currentTime.getMinutes();
    return (currentHour > 8) || (currentHour === 8 && currentMinutes >= 5); // After 8:05 AM
  };

  const meetingStatus = getMeetingStatus();

  // If the thank you screen should be shown
  if (showThankYou) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 flex items-center justify-center">
        <div className="max-w-lg w-full">
          <motion.div 
            initial={{ opacity: 0, y: -20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.5 }}
            className="bg-white rounded-xl shadow-lg overflow-hidden text-center p-8"
          >
            <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <SafeIcon icon={FiCheckCircle} className="text-white text-3xl" />
            </div>
            
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Thank You!</h2>
            <p className="text-gray-700 text-lg mb-6">
              Your check-in has been successfully submitted.
            </p>
            <p className="text-gray-600">
              You may close this page now.
            </p>
            
            <div className="mt-8 text-sm text-gray-500">
              <p>Montgomery County Professional Networking Group</p>
              <p className="mt-1">&copy; {new Date().getFullYear()} MOCO PNG</p>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4">
      <div className="max-w-lg mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-xl shadow-lg overflow-hidden mb-6"
        >
          <div className="bg-gradient-to-r from-brand-primary to-brand-secondary text-white p-6">
            <div className="flex items-center justify-center">
              <h1 className="text-2xl font-bold">MOCO PNG</h1>
            </div>
            <p className="text-center mt-2">Montgomery County Professional Networking Group</p>
          </div>
          <div className="p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4 text-center">Member Check-In</h2>
            <div className={`p-4 rounded-lg mb-6 ${meetingStatus.bgColor}`}>
              <p className={`text-center font-medium ${meetingStatus.color}`}>
                {meetingStatus.message}
              </p>
              <p className="text-center text-sm mt-1">
                {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </div>

            {!isCheckInAllowed && (
              <div className="bg-gray-50 border border-gray-300 rounded-lg p-6 mb-6 text-center">
                <div className="w-16 h-16 bg-gray-400 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiLock} className="text-white text-2xl" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Check-In Not Available</h3>
                <p className="text-gray-600 mb-2">
                  Check-in is only available on Tuesdays from 7:00 AM to 9:00 AM.
                </p>
                <div className="flex flex-col space-y-2 mt-4">
                  <div className="flex items-center justify-center space-x-2 text-gray-600">
                    <SafeIcon icon={FiCalendar} />
                    <span>Available days: Tuesday only</span>
                  </div>
                  <div className="flex items-center justify-center space-x-2 text-gray-600">
                    <SafeIcon icon={FiClock} />
                    <span>Available times: 7:00 AM - 9:00 AM</span>
                  </div>
                </div>
              </div>
            )}

            {checkInSuccess && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6"
              >
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mr-3">
                    <SafeIcon icon={FiCheckCircle} className="text-white text-xl" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-green-800 text-lg">Check-in Successful!</h4>
                    <p className="text-green-700">
                      You have been checked in to today's meeting.
                    </p>
                    {isLate() && (
                      <p className="text-orange-600 mt-1">
                        Note: You were checked in as late (after 8:05 AM).
                      </p>
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {checkInError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center mr-3">
                    <SafeIcon icon={FiAlertCircle} className="text-white text-xl" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-red-800 text-lg">Check-in Failed</h4>
                    <p className="text-red-700">{checkInError}</p>
                  </div>
                </div>
              </div>
            )}

            {!checkInSuccess && (
              <div>
                <div className="relative mb-4">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <SafeIcon icon={FiSearch} className="text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className={`w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent ${!isCheckInAllowed ? 'bg-gray-100' : ''}`}
                    placeholder="Search your name..."
                    disabled={!isCheckInAllowed}
                  />
                </div>

                <div className={`border border-gray-200 rounded-lg overflow-hidden max-h-60 overflow-y-auto mb-4 ${!isCheckInAllowed ? 'opacity-75' : ''}`}>
                  {filteredMembers.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">
                      <SafeIcon icon={FiUsers} className="mx-auto text-3xl mb-2" />
                      <p>No members found. Try a different search term.</p>
                    </div>
                  ) : (
                    <div>
                      {filteredMembers.map(member => {
                        const isCheckedIn = todaysMeeting?.attendees?.some(a => a.memberId === member.id);
                        return (
                          <div
                            key={member.id}
                            className={`p-4 border-b border-gray-100 last:border-b-0 ${isCheckInAllowed ? 'cursor-pointer' : 'cursor-not-allowed'} transition-colors ${
                              selectedMember?.id === member.id
                                ? 'bg-blue-50'
                                : isCheckedIn
                                ? 'bg-green-50'
                                : isCheckInAllowed
                                ? 'hover:bg-gray-50'
                                : ''
                            }`}
                            onClick={() => !isCheckedIn && handleSelectMember(member)}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium">{member.name}</p>
                                {member.company && (
                                  <p className="text-xs text-gray-600">{member.company}</p>
                                )}
                              </div>
                              {isCheckedIn && (
                                <div className="flex items-center text-green-600">
                                  <SafeIcon icon={FiCheck} className="mr-1" />
                                  <span className="text-sm">Checked In</span>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {selectedMember && (
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200 mb-4">
                <h4 className="font-semibold text-blue-800">Selected Member</h4>
                <p className="text-blue-700">{selectedMember.name}</p>
                {selectedMember.company && (
                  <p className="text-sm text-blue-600">{selectedMember.company}</p>
                )}
                <div className="mt-4 flex items-center text-sm">
                  <SafeIcon icon={FiClock} className="mr-1 text-blue-600" />
                  <span className="text-blue-700">
                    Check-in time: {currentTime.toLocaleTimeString()} {isLate() ? ' (Late)' : ' (On Time)'}
                  </span>
                </div>
              </div>
            )}

            <button
              onClick={handleCheckIn}
              disabled={!selectedMember || !isCheckInAllowed}
              className={`w-full py-3 px-6 rounded-lg font-semibold flex items-center justify-center ${
                selectedMember && isCheckInAllowed
                  ? 'bg-brand-primary hover:bg-brand-secondary text-white'
                  : 'bg-gray-200 text-gray-500 cursor-not-allowed'
              }`}
            >
              <SafeIcon icon={FiCheck} className="mr-2" />
              Check In
            </button>

            {!isCheckInAllowed && (
              <p className="text-center text-sm text-red-600 mt-2">
                Check-in is only available on Tuesdays between 7:00 AM and 9:00 AM
              </p>
            )}
          </div>
        </motion.div>
        <div className="text-center text-sm text-gray-500">
          <p>Having trouble? Please see the meeting coordinator.</p>
          <p className="mt-1">
            Montgomery County Professional Networking Group &copy;{new Date().getFullYear()}
          </p>
        </div>
      </div>
    </div>
  );
};

export default QRCheckIn;